import { useContext, useEffect, useEffectEvent, useState } from 'react';

import { getEnquiryData, postIncodeVerificationDocuments, postSaleVerifications } from 'PrivateApis';

import { ConfigContext } from 'Context/config';
import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { PremiumOffer } from 'Utilities/profiling/@types/premiumOffer';

import { authorizationHeader } from 'Containers/app/vehicleCollection/services/apiClient';

import { INCODE_ANALYTICS } from '../IncodeAnalytics';
import { IncodeSession, IncodeSessionAPIResponse, SENTRY_TAG } from '../IncodeSDKHelpers';

import { IncodeSessionState } from './useIncodeVerificationSession';

type Payload = {
  address: string;
  enquiryId: string;
  isOnFinance: boolean;
  name?: string | null;
  postcode?: string | null;
  requiredModules?: {
    keeperAuthorization: {
      keeper: {
        name: string;
        type: PremiumOffer['vehicleDetails']['keeperType'];
      };
      required: true;
    };
  };
  sellerId: string;
  vrm: string | undefined;
}

export const useEarlyDocsIncodeVerificationDocuments = () => {
  const { userState: user } = useContext(UserContext);
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);

  const [incodeSession, setIncodeSession] = useState<IncodeSessionState>(
    { session: undefined, success: undefined },
  );

  const { configState: { featureFlags } } = useContext(ConfigContext);
  const useV2 = !!featureFlags?.useV2SaleVerifications; // AUT-1136

  const { auth_token: authToken, dpSellerId, name, postcode } = user ?? {};
  const { dpEnquiryId } = vehicle?.enquiry ?? {};

  const buildPayload = useEffectEvent((dpEnquiry: PremiumOffer): Payload => {
    const payload: Payload = {
      address: user?.address1 ?? '',
      enquiryId: String(dpEnquiryId),
      isOnFinance: !!dpEnquiry.vehicleDetails?.isVehicleOnFinance,
      name,
      postcode,
      sellerId: String(dpSellerId),
      vrm: vehicle?.vrm,
    };

    /**
     * Backend have confirmed that seller does not need a module, and anything other than
     * familyOrFriend will be blocked - we're intentionally keeping the logic on the backend
     */
    const keeperType = dpEnquiry?.vehicleDetails?.keeperType;
    if (keeperType && keeperType !== 'seller') {
      payload.requiredModules = {
        keeperAuthorization: {
          keeper: {
            name: dpEnquiry?.vehicleDetails?.keeperName ?? '',
            type: keeperType,
          },
          required: true,
        },
      };
    }

    return payload;
  });

  useEffect(() => {
    const abortController = new AbortController();

    if (!authToken || !dpEnquiryId) {
      setIncodeSession({ session: undefined, success: false });
      return () => {};
    }

    const request = useV2
      ? postSaleVerifications(
        authorizationHeader(authToken),
        { enquiryId: dpEnquiryId },
        abortController,
      )
      : getEnquiryData(dpEnquiryId, {}, 'vehicleDetails', abortController)
        .then((dpEnquiry: PremiumOffer) => {
          if (abortController.signal.aborted) {
            return undefined;
          }

          return postIncodeVerificationDocuments(
            { Authorization: authToken },
            buildPayload(dpEnquiry),
            abortController,
          );
        });

    request
      .then((res: IncodeSessionAPIResponse | undefined) => {
        if (!res || abortController.signal.aborted) {
          return;
        }

        if (!res?.data?.sellerVerificationSession) {
          throw new Error('No incode session returned');
        }

        const { onboardingToken, ...rest } = res.data.sellerVerificationSession;
        const session: IncodeSession = { token: onboardingToken, ...rest };

        INCODE_ANALYTICS.INCODE_ADD_CONTEXT(session);
        setIncodeSession({ session, success: true });
      })
      .catch((err) => {
        if (err.name === 'AbortError') {
          return;
        }

        globalThis?.Sentry?.captureException?.(err, SENTRY_TAG);
        setIncodeSession({ session: undefined, success: false });
      });

    return () => {
      abortController?.abort();
    };
  }, [authToken, dpEnquiryId, useV2]);

  return { incodeSession };
};
